import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useToolShedToast } from '@phoenix/team-m/my-tool-shed/frontend/ui/notifications/useToast';
import { FeedbackMode } from '@phoenix/core/frontend/shared/ui/feedback';
import { EmptyToolShed } from '@phoenix/team-m/my-tool-shed/frontend/ui/overview/empty-tool-shed';
import { LoadingErrorMessage, PaginationLoader } from '@phoenix/team-m/my-tool-shed/frontend/ui/common';
import { ServiceReminderPopUp } from '@phoenix/team-m/dashboard/frontend/shared/notifications/service-reminder-pop-up';
import { ToolShedFeedbackConfig, useToolShedFeedback } from '@phoenix/team-m/my-tool-shed/frontend/shared/use-tool-shed-feedback';
import { usePaginatedUserProducts } from '@phoenix/team-m/my-tool-shed/frontend/shared/user-products';
import { CustomerSupportAlert } from '@phoenix/team-m/my-tool-shed/frontend/shared/customer-support/customer-support-alert';
import { ToolShedPage } from '@phoenix/team-m/my-tool-shed/frontend/shared/tool-shed-page';

import { useAddToolSessionStorage } from '../add-products/use-add-tool-session-storage';
import { ToolShedProductPage } from './tool-shed-products-page';
import { ToolAutomaticSearch } from './automatic-search/tool-shed-automatic-search';
import { BenefitsSlider } from './benefits/benefits-slider';

export const ToolShedOverview: FC = () => {
  const { t } = useTranslation();
  const { t: tShared } = useTranslation('shared');
  const { showSuccessToast } = useToolShedToast();
  const { newToolIds, isWarrantyExtensionApplied, clearNewTool } = useAddToolSessionStorage();
  const { 'customer-support-hint': customerSupportHintFlagEnabled, raffle: raffleEnabled, voucher: voucherEnabled } = useFlags();
  const { pages, isLoading, isFetchingNextPage, isError, hasNextPage, fetchNextPage, isEmpty } = usePaginatedUserProducts();

  useEffect(() => {
    const shouldShowAddToolSuccessMessage = !!newToolIds.length && isEmpty === false;

    shouldShowAddToolSuccessMessage &&
      showSuccessToast(t(isWarrantyExtensionApplied ? 'addTool_toast_success_added_emailConfirmation' : 'addTool_toast_success_added'));
  }, [newToolIds.length, isEmpty]);

  const feedbackConfig: ToolShedFeedbackConfig = useMemo(
    () =>
      newToolIds.length
        ? {
            feedbackMode: FeedbackMode.Thumbs,
            question: t('userFeedback_addTool_like'),
            feedbackLabels: [tShared('userFeedback_scaleLike_label_negative'), tShared('userFeedback_scaleLike_label_positive')],
            onPage: 'addTool',
          }
        : {
            question: t('userFeedback_toolShed_overview_satisfaction'),
            showAtSecondVisit: true,
          },
    [newToolIds.length]
  );

  const { setIsViewReady: setViewIsReadyForFeedback } = useToolShedFeedback(feedbackConfig);

  useEffect(() => clearNewTool, []);

  useEffect(() => {
    setViewIsReadyForFeedback(!newToolIds.length && isEmpty === false);
  }, [newToolIds.length, isEmpty]);
  return (
    <ToolShedPage title={t('toolShed_overview_headline')} pageTestId={'toolshed-overview-page'}>
      <p className="mt-3 stihl-text-body-default-reg tablet:mt-0">{t('toolShed_overview_subHeadline')}</p>
      {isEmpty ? (
        <>
          <EmptyToolShed />
          <ToolAutomaticSearch className="desktop:mt-12" />
          {(raffleEnabled || voucherEnabled) && <BenefitsSlider />}
        </>
      ) : (
        <>
          <ToolAutomaticSearch className="desktop:mt-6" />
          {customerSupportHintFlagEnabled && (
            <CustomerSupportAlert className="mt-8" message={t('toolshed_customerSuppport_notification_text')} />
          )}
          {(raffleEnabled || voucherEnabled) && <BenefitsSlider />}
          {isError ? (
            <LoadingErrorMessage className="mt-20 tablet:mt-[10.5rem]" message={t('toolShed_overview_failedToLoad')} />
          ) : (
            <div data-testid="productList" className="mt-0 desktop:mt-4">
              {isLoading ? (
                <ToolShedProductPage isLoading={isLoading} />
              ) : (
                pages?.map((products, index) => (
                  <ToolShedProductPage key={index} products={products} newToolIds={newToolIds} isLoading={isLoading} />
                ))
              )}
              <PaginationLoader
                id="myTools"
                className="mt-8 pb-0"
                hasNextPage={!!hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onClickLoadMore={fetchNextPage}
              />
            </div>
          )}
        </>
      )}
      <ServiceReminderPopUp />
    </ToolShedPage>
  );
};
